import React, { useState } from 'react';
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  Popconfirm,
  Row,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import {
  ScissorOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { WriteoffCandidateItem, c5n4Api } from '../../services/c5n4Api';
import {
  BatchAcceptanceBridge,
  pwc5n4f1AcceptanceManifest,
  useAcceptanceMode,
} from '../../features/acceptance';
import { useC5N4 } from './hooks/useC5N4';
import C5N4DetailDrawer, { detailColumns as c5n4DetailColumns } from './components/C5N4DetailDrawer';
import { useModulePermission } from '../../hooks/usePermission';
import './C5N4Writeoff.css';

const { Title, Text } = Typography;
const acceptanceActionIds = {
  list: 'PWC5N4F1-ACT-QUERY',
  writeoff: 'PWC5N4F1-ACT-REVERSE',
};
void acceptanceActionIds;

// ============================================================
// Helpers
// ============================================================

function renderAbnormalReason(code: string): React.ReactNode {
  if (code === 'P') return <Tag color="orange">單價異常</Tag>;
  if (code === 'Q') return <Tag color="blue">數量超交</Tag>;
  if (code === 'L') return <Tag color="red">單價異常及數量超交</Tag>;
  return <Tag color="default">{code || '—'}</Tag>;
}

function renderApprovalStatus(stat: string): React.ReactNode {
  if (stat === 'D') return <Tag color="orange">送審中</Tag>;
  if (stat === 'P') return <Tag color="green">已核准</Tag>;
  if (stat === 'N') return <Tag color="red">已沖銷</Tag>;
  return <Tag color="default">{stat || '—'}</Tag>;
}

function formatDate(date: string | null | undefined): string {
  if (!date) return '—';
  try {
    return new Date(date).toLocaleDateString('zh-TW');
  } catch {
    return date;
  }
}

function formatNumber(value: number, decimals = 2): string {
  if (!value && value !== 0) return '—';
  return value.toLocaleString('zh-TW', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

// ============================================================
// Main Component
// ============================================================

const C5N4Writeoff: React.FC = () => {
  const acceptanceMode = useAcceptanceMode();
  const [detailSchemaPreview, setDetailSchemaPreview] = useState(false);
  const {
    loading,
    writeoffing,
    candidateItems,
    total,
    selectedKeys,
    filter,
    detailData,
    detailLoading,
    detailVisible,
    setFilter,
    setSelectedKeys,
    fetchCandidates,
    batchWriteoff,
    loadDetail,
    closeDetail,
  } = useC5N4();

  const permissions = useModulePermission('pwc5n4f1');

  // ============================================================
  // Table columns
  // ============================================================
  const columns: ColumnsType<WriteoffCandidateItem> = [
    {
      title: <span data-contract-field="fc120_01">交運單號</span>,
      dataIndex: 'do_no',
      key: 'do_no',
      width: 100,
      render: (text: string) => (
        <span
          className="c5n4-do-no-link"
          onClick={() => loadDetail(text)}
          title="點擊查看明細"
        >
          {text}
        </span>
      ),
    },
    {
      title: <span data-contract-field="fc120_10">S/C No</span>,
      dataIndex: 'sc_no',
      key: 'sc_no',
      width: 100,
    },
    {
      title: <span data-contract-field="fc120_11">交運日期</span>,
      dataIndex: 'do_date',
      key: 'do_date',
      width: 100,
      render: (date: string | null) => formatDate(date),
    },
    {
      title: <span data-contract-field="ui_customer_name">客戶名稱</span>,
      dataIndex: 'customer_name',
      key: 'customer_name',
      ellipsis: true,
    },
    {
      title: <span data-contract-field="ui_dept_name">部門</span>,
      dataIndex: 'dept_name',
      key: 'dept_name',
      width: 100,
      ellipsis: true,
    },
    {
      title: <span data-contract-field="fc120_18">幣別</span>,
      dataIndex: 'currency',
      key: 'currency',
      width: 60,
      align: 'center',
    },
    {
      title: <span data-contract-field="fc120_20">未稅金額</span>,
      dataIndex: 'untaxed_amount',
      key: 'untaxed_amount',
      width: 130,
      align: 'right',
      render: (v: number) => formatNumber(v),
    },
    {
      title: <span data-contract-field="fc120_201">含稅總額</span>,
      dataIndex: 'taxed_amount',
      key: 'taxed_amount',
      width: 130,
      align: 'right',
      render: (v: number) => formatNumber(v),
    },
    {
      title: <span data-contract-field="fc120_24">異常原因</span>,
      dataIndex: 'abnormal_reason',
      key: 'abnormal_reason',
      width: 160,
      render: (code: string) => renderAbnormalReason(code),
    },
    {
      title: <span data-contract-field="fc120_80">狀態</span>,
      dataIndex: 'approval_status',
      key: 'approval_status',
      width: 80,
      align: 'center',
      render: renderApprovalStatus,
    },
  ];

  const rowSelection = {
    selectedRowKeys: selectedKeys,
    onChange: (keys: React.Key[]) => setSelectedKeys(keys as string[]),
  };

  const handleSearch = () => { fetchCandidates(); };
  const handleFilterChange = (field: 'doStart' | 'doEnd', value: string) => {
    setFilter({ ...filter, [field]: value });
  };

  return (
    <div className="c5n4-container">
      <Title level={4} style={{ marginBottom: 16 }}>異常交運單核准沖銷 (pwc5n4f1)</Title>

      {/* Filter Bar */}
      <Card size="small" style={{ marginBottom: 12 }}>
        <Form layout="inline" size="small">
          <Form.Item label={<span data-contract-field="query_do_start">交運單號 從</span>}>
            <Input
              value={filter.doStart}
              onChange={(e) => handleFilterChange('doStart', e.target.value)}
              placeholder="起始單號"
              style={{ width: 120 }}
              onPressEnter={handleSearch}
            />
          </Form.Item>
          <Form.Item label={<span data-contract-field="query_do_end">至</span>}>
            <Input
              value={filter.doEnd}
              onChange={(e) => handleFilterChange('doEnd', e.target.value)}
              placeholder="結尾單號"
              style={{ width: 120 }}
              onPressEnter={handleSearch}
            />
          </Form.Item>
          <Form.Item>
            <Button
              type="primary"
              icon={<SearchOutlined />}
              data-acceptance-action="PWC5N4F1-ACT-QUERY"
              onClick={handleSearch}
              loading={loading}
              disabled={!permissions.canView}
            >
              確定範圍
            </Button>
          </Form.Item>
        </Form>
      </Card>

      {/* Summary & Actions */}
      <Card size="small" style={{ marginBottom: 12 }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Space>
              <Text type="secondary">
                共 <strong>{total}</strong> 筆可沖銷
              </Text>
              {selectedKeys.length > 0 && (
                <Text type="secondary">
                  已勾選 <strong>{selectedKeys.length}</strong> 筆
                </Text>
              )}
            </Space>
          </Col>
          <Col>
            <Popconfirm
              title={`確認沖銷 ${selectedKeys.length} 筆異常交運單？`}
              description="沖銷後將反轉 C514 核准的副作用（庫存、應收帳款等），此操作無法撤銷。"
              onConfirm={batchWriteoff}
              okText="確認沖銷"
              cancelText="取消"
              disabled={selectedKeys.length === 0}
            >
              <Button
                danger
                icon={<ScissorOutlined />}
                data-acceptance-action="PWC5N4F1-ACT-REVERSE"
                loading={writeoffing}
                disabled={selectedKeys.length === 0 || !permissions.canDelete}
              >
                存檔（批量沖銷）
              </Button>
            </Popconfirm>
          </Col>
        </Row>
      </Card>

      {/* Data Table */}
      <Table<WriteoffCandidateItem>
        rowKey="do_no"
        columns={columns}
        dataSource={candidateItems}
        loading={loading}
        rowSelection={rowSelection}
        size="small"
        pagination={{ pageSize: 50, showSizeChanger: false, showTotal: (t) => `共 ${t} 筆` }}
        scroll={{ x: 1000 }}
        locale={{ emptyText: '無可沖銷資料，請先點擊「確定範圍」查詢' }}
      />

      <C5N4DetailDrawer
        visible={detailVisible}
        loading={detailLoading}
        data={detailData}
        onClose={() => {
          setDetailSchemaPreview(false);
          closeDetail();
        }}
      />
      {acceptanceMode.isEnabled(pwc5n4f1AcceptanceManifest.formId) && (
        <>
          <label data-acceptance-non-field style={{ display: 'inline-flex', gap: 6, marginTop: 8 }}>
            <input
              type="checkbox"
              data-contract-field="ui_detail_selected"
              checked={detailSchemaPreview}
              onChange={event => setDetailSchemaPreview(event.target.checked)}
            />
            驗收：顯示明細欄位結構
          </label>
          {detailSchemaPreview && (
            <Table
              rowKey="fc121_02"
              columns={c5n4DetailColumns}
              dataSource={[]}
              size="small"
              pagination={false}
              style={{ marginTop: 8 }}
            />
          )}
        </>
      )}
      <BatchAcceptanceBridge
        manifest={pwc5n4f1AcceptanceManifest}
        records={candidateItems}
        selectedKeys={selectedKeys}
        keyOf={(item) => item.do_no}
        queryExact={(key) => c5n4Api.getDetail(key)}
        lastApiResponse={{ items: candidateItems, total }}
        loading={loading || writeoffing}
        onAutoFill={() => {
          const first = candidateItems[0];
          if (first) setSelectedKeys([first.do_no]);
        }}
        autoFillDisabled={candidateItems.length === 0}
        autoFillReason="先查詢一筆可沖銷的異常交運單，再由驗收模式勾選。"
        screenDraft={{ filter, selected_items: selectedKeys }}
      />
    </div>
  );
};

export default C5N4Writeoff;
